import {
  readMessageBodyCache,
  readMessageBodyCacheAsync,
} from "@/lib/cache/messageBodyCache";
import { primeMessageBodyFromServer } from "@/lib/mail/fetchMessageBodyWithCache";
import { isFetchedBodyComplete } from "@/lib/mail/messageBodyCacheStatus";
import type { MailMessageDetail, MailMessageSummary } from "@/lib/types/mail";

type BodyCacheCandidate = { folder: string; uid: number };

export function bodyCacheCandidates(
  accountId: number,
  folder: string,
  uid: number,
  preview?: MailMessageSummary | null
): BodyCacheCandidate[] {
  const seen = new Set<string>();
  const out: BodyCacheCandidate[] = [];
  const add = (fold: string, u: number) => {
    if (u <= 0 || !fold) return;
    const key = `${accountId}\0${fold}\0${u}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ folder: fold, uid: u });
  };

  add(folder, uid);
  if (!preview) return out;

  const listUid = preview.uid;
  const bodyUid = preview.imapBodyUid ?? preview.uid;
  const altFolder = preview.imapFolder?.trim();

  if (listUid > 0) add(folder, listUid);
  if (bodyUid > 0) add(folder, bodyUid);
  if (altFolder) {
    add(altFolder, bodyUid);
    if (listUid > 0 && listUid !== bodyUid) add(altFolder, listUid);
  }

  return out;
}

/** Lecture synchrone — mémoire uniquement (toutes clés candidates). */
export function readCachedMessageBody(
  accountId: number,
  folder: string,
  uid: number,
  preview?: MailMessageSummary | null
): MailMessageDetail | null {
  for (const candidate of bodyCacheCandidates(accountId, folder, uid, preview)) {
    const hit = readMessageBodyCache(accountId, candidate.folder, candidate.uid);
    if (hit && isFetchedBodyComplete(hit)) return hit;
  }
  return null;
}

/** Lecture async — mémoire puis IndexedDB (0 IMAP si hit). */
export async function readCachedMessageBodyAsync(
  accountId: number,
  folder: string,
  uid: number,
  preview?: MailMessageSummary | null
): Promise<MailMessageDetail | null> {
  for (const candidate of bodyCacheCandidates(accountId, folder, uid, preview)) {
    const hit = await readMessageBodyCacheAsync(accountId, candidate.folder, candidate.uid);
    if (hit && isFetchedBodyComplete(hit)) return hit;
  }
  return null;
}

/**
 * Préchauffe au survol uniquement — ne jamais bloquer le clic.
 * Mémoire → IDB → 1 check DD02 rapide.
 */
export async function warmMessageBodyForOpen(
  accountId: number,
  folder: string,
  uid: number,
  preview?: MailMessageSummary | null
): Promise<boolean> {
  if (uid <= 0 || accountId <= 0) return false;

  const memoryHit = readCachedMessageBody(accountId, folder, uid, preview);
  if (memoryHit) return true;

  const idbHit = await readCachedMessageBodyAsync(accountId, folder, uid, preview);
  if (idbHit) return true;

  await primeMessageBodyFromServer(accountId, folder, uid, {
    dd02Only: true,
    quick: true,
    timeoutMs: 6_000,
  });
  return readCachedMessageBody(accountId, folder, uid, preview) != null;
}
